// jobDashboardService.js
import axios from 'axios';

/**
 * Helper function to get auth headers
 * @returns {Object} Auth headers
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Fetch current user information
export const fetchCurrentUser = async () => {
  try {
    // Use the correct API endpoint path based on server.js configuration
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
      headers: getAuthHeader()
    });

    if (response.data) {
      // console.log('Current user fetched successfully:', response.data); // Removed log
      return response.data;
    }
  } catch (error) {
    // console.error('Error fetching user profile:', error); // Removed log
    // Try alternative endpoint if the first one fails
    try {
      const altResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        headers: getAuthHeader()
      });

      if (altResponse.data) {
        // console.log('Current user fetched from alternative endpoint:', altResponse.data); // Removed log
        return altResponse.data;
      }
    } catch (altError) {
      // console.error('Error fetching user profile from alternative endpoint:', altError); // Removed log
      throw altError;
    }
  }
};

// Fetch jobs based on category and filters
export const fetchJobs = async (jobCategory, filters = {}, currentJobs = [], pageNum = 0, rowsPerPageNum = 10) => { // Add filters and pagination parameters
  try {
    // console.log(`Fetching jobs for category: ${jobCategory} with filters:`, filters, `page: ${pageNum}`, `limit: ${rowsPerPageNum}`); // Optional debug log

    // Construct query parameters
    const params = new URLSearchParams();
    params.set('category', jobCategory);
    params.set('page', pageNum + 1); // API is 1-based for page
    params.set('limit', rowsPerPageNum);


    // Add filters to parameters if they have values
    for (const key in filters) {
      if (filters[key]) { // Check if the filter value is truthy (not empty string, null, undefined)
        params.set(key, filters[key]);
      }
    }

    // Construct the final URL
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/jobs?${params.toString()}`; // Use /api/jobs and add params
    // console.log(`Fetching jobs from URL: ${apiUrl}`); // Optional debug log

    const response = await axios.get(apiUrl, { // Use the constructed URL
      headers: getAuthHeader()
    });

    // Assuming API returns { jobs: [], totalCount: X }
    const responseData = response.data;
    const jobsData = responseData.jobs || []; // Fallback to empty array if jobs is not present
    const totalCount = responseData.totalCount || 0; // Fallback to 0 if totalCount is not present

    // console.log(`Received ${jobsData.length} jobs, totalCount: ${totalCount}:`, jobsData); // Removed log

    // Process jobs to ensure they have the expanded property
    const processedJobs = jobsData.map(job => {
      // Find the job in current state if it exists
      const existingJob = currentJobs.find(j => j.id === job.id);

      // Preserve expanded state if the job already exists in our state
      // Otherwise initialize as collapsed
      const expanded = existingJob ? existingJob.expanded : false;

      let createdDisplay;

      // Check if createdAt exists and is a valid date
      if (job.createdAt && !isNaN(new Date(job.createdAt).getTime())) {
        const createdDate = new Date(job.createdAt);
        const today = new Date();

        // Compare year, month, and day to determine if it's today
        const isCreatedToday =
          createdDate.getFullYear() === today.getFullYear() &&
          createdDate.getMonth() === today.getMonth() &&
          createdDate.getDate() === today.getDate();

        if (isCreatedToday) {
          // If created today, show time
          createdDisplay = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
          // If created on a different day, show date
          createdDisplay = createdDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        }
      } else {
        // Fallback to the existing created field or 'Unknown'
        createdDisplay = job.created || 'Unknown';
      }

      return {
        ...job,
        expanded,
        created: createdDisplay
      };
    });

    return { jobs: processedJobs, totalCount };
  } catch (err) {
    // console.error('Error fetching jobs:', err); // Removed log
    throw err;
  }
};

// Fetch drivers
export const fetchDrivers = async () => {
  try {
    // Only fetch drivers who are on duty
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers?isOnDuty=true`, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (err) {
    // console.error('Error fetching drivers:', err); // Removed log
    throw err;
  }
};

// Accept job
export const acceptJob = async (jobId, etaValue) => {
  try {
    // Use the specific accept endpoint with the ETA
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/accept`, {
      eta: etaValue
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    // console.error('Error accepting job:', error); // Removed log
    throw error;
  }
};

// Direct accept job (without showing ETA dialog)
export const directAcceptJob = async (jobId, existingEta = "30") => {
  try {
    // Accept the job with the existing ETA or default to 30 minutes
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/accept`, { eta: existingEta }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    console.error('Error accepting job:', error);
    throw error;
  }
};

// Blast dispatch: broadcast the job offer to all SP facilities and on-duty
// Org 1 drivers within a radius; first to accept wins
export const blastJob = async (jobId, radiusMiles) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/blast`, {
      radiusMiles
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error starting blast dispatch:', error);
    throw error;
  }
};

// Email temporary card payment details to the provider of a completed credit-card job
export const sendCardPayment = async (jobId, cardDetails) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/card-payment`, cardDetails, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error sending card payment:', error);
    throw error;
  }
};

// Stop an active blast (or dismiss a failed-blast response window)
export const stopBlastJob = async (jobId) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/blast/stop`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error stopping blast dispatch:', error);
    throw error;
  }
};

// NEW: Accept job offer by SP
export const acceptJobBySP = async (jobId, etaValue) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/sp-accept`, {
      eta: etaValue
    }, {
      headers: getAuthHeader()
    });
    // console.log(`Job ${jobId} accepted by SP successfully:`, response.data); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error accepting job by SP:', error); // Removed log
    throw error;
  }
};

// Reject job with reason
export const rejectJob = async (jobId, rejectionReason) => {
  try {
    // console.log(`Rejecting job ${jobId} with reason: ${rejectionReason}`); // Removed log

    // Use the dedicated reject endpoint instead of the general status endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/reject`, {
      rejectionReason
    }, {
      headers: getAuthHeader()
    });

    // console.log(`Job ${jobId} rejected successfully:`, response.data); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error rejecting job:', error); // Removed log
    // console.error('Error details:', error.response?.data || error.message); // Removed log
    throw error;
  }
};

// NEW: Reject job offer by SP
export const rejectJobBySP = async (jobId, rejectionReason) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/sp-reject`, {
      rejectionReason
    }, {
      headers: getAuthHeader()
    });
    // console.log(`Job ${jobId} rejected by SP successfully:`, response.data); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error rejecting job by SP:', error); // Removed log
    throw error;
  }
};


// Assign driver and truck to job
export const assignDriverAndTruck = async (jobId, driverId, truck, preserveAssignedAt = false, originalAssignedAt = null) => {
  try {
    // Prepare request data
    const requestData = {
      driverId,
      truck
    };

    // If this is a redispatch, preserve the original assignedAt time
    if (preserveAssignedAt && originalAssignedAt) {
      requestData.preserveAssignedAt = true;
      requestData.originalAssignedAt = originalAssignedAt;
    }

    // Update job on the server
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/assign`, requestData, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    // Log the actual error details to help with debugging
    console.error('Error assigning driver and truck:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Update job status
export const updateJobStatus = async (jobId, status) => {
  try {
    // Update job status on the server using the dedicated status endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/status`, {
      status
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    // console.error('Error canceling job:', error); // Removed log
    throw error;
  }
};

// Cancel job with reason
export const cancelJob = async (jobId, cancellationReason) => {
  try {
    // Update job status on the server using the dedicated status endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/status`, {
      status: 'Canceled',
      cancellationReason
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    // console.error('Error marking job as GOA:', error); // Removed log
    throw error;
  }
};

// Mark job as GOA with reason
export const markJobAsGOA = async (jobId, goaReason) => {
  try {
    // Update job status on the server using the dedicated status endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/status`, {
      status: 'Awaiting Approval',
      goaReason
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    // console.error('Error marking job as GOA:', error); // Removed log
    throw error;
  }
};

// Report job as unsuccessful with reason
export const reportJobUnsuccessful = async (jobId, unsuccessfulReason) => {
  try {
    // Use the dedicated unsuccessful endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/unsuccessful`, {
      unsuccessfulReason
    }, {
      headers: getAuthHeader()
    });

    // console.log(`Job ${jobId} marked as unsuccessful with reason: ${unsuccessfulReason}`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error marking job as unsuccessful:', error); // Removed log
    throw error;
  }
};

// Approve unsuccessful request
export const approveUnsuccessfulJob = async (jobId) => {
  try {
    // Use the dedicated approve unsuccessful endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/unsuccessful/approve`, {}, {
      headers: getAuthHeader()
    });

    // console.log(`Unsuccessful request for job ${jobId} approved successfully`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error approving unsuccessful request:', error); // Removed log
    throw error;
  }
};

// Deny unsuccessful request
export const denyUnsuccessfulJob = async (jobId) => {
  try {
    // Use the dedicated deny unsuccessful endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/unsuccessful/deny`, {}, {
      headers: getAuthHeader()
    });

    // console.log(`Unsuccessful request for job ${jobId} denied successfully`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error denying unsuccessful request:', error); // Removed log
    throw error;
  }
};

// Approve GOA request
export const approveGOA = async (jobId) => {
  try {
    // Use the dedicated approve GOA endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/goa/approve`, {}, {
      headers: getAuthHeader()
    });

    // console.log(`GOA request for job ${jobId} approved successfully`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error approving GOA request:', error); // Removed log
    throw error;
  }
};

// Deny GOA request
export const denyGOA = async (jobId) => {
  try {
    // Use the dedicated deny GOA endpoint
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/goa/deny`, {}, {
      headers: getAuthHeader()
    });

    // console.log(`GOA request for job ${jobId} denied successfully`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error denying GOA request:', error); // Removed log
    throw error;
  }
};

// Approve SP ETA
export const approveETA = async (jobId) => {
  console.log(`[jobDashboardService] Attempting to approve ETA for job: ${jobId}`);
  try {
    // Use the ETA decision endpoint
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/eta-decision`, {
      decision: 'accept' // Changed back to 'accept' to match what the controller expects
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error(`[jobDashboardService] Error approving ETA for job: ${jobId}`, error.response?.data || error.message);
    console.error(`[jobDashboardService] Full error object:`, error);
    throw error;
  }
};

// Deny SP ETA with reason
export const denyETA = async (jobId, reason) => {
  console.log(`[jobDashboardService] Attempting to deny ETA for job: ${jobId} with reason: ${reason}`);
  try {
    // Use the ETA decision endpoint
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/eta-decision`, {
      decision: 'reject', // Changed back to 'reject' to match what the controller expects
      reason: reason || 'ETA not acceptable'
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error(`[jobDashboardService] Error denying ETA for job: ${jobId}`, error.response?.data || error.message);
    console.error(`[jobDashboardService] Full error object:`, error);
    throw error;
  }
};

// Approve Org 1 Driver ETA
export const approveOrg1DriverETA = async (jobId) => {
  console.log(`[jobDashboardService] Attempting to approve Org 1 Driver ETA for job: ${jobId}`);
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/org1-driver-eta-decision`, {
      decision: 'accept'
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error(`[jobDashboardService] Error approving Org 1 Driver ETA for job: ${jobId}`, error.response?.data || error.message);
    throw error;
  }
};

// Reject Org 1 Driver ETA
export const rejectOrg1DriverETA = async (jobId) => {
  console.log(`[jobDashboardService] Attempting to reject Org 1 Driver ETA for job: ${jobId}`);
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/org1-driver-eta-decision`, {
      decision: 'reject'
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error(`[jobDashboardService] Error rejecting Org 1 Driver ETA for job: ${jobId}`, error.response?.data || error.message);
    throw error;
  }
};

// Update job ETA
export const updateETA = async (jobId, newEtaValue) => {
  try {
    // Update job ETA on the server using the main endpoint
    // We use the main endpoint here because ETA is a job detail, not a status
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`, {
      eta: newEtaValue
    }, {
      headers: getAuthHeader()
    });

    // console.log(`Updated ETA for job ${jobId} to ${newEtaValue} minutes`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error updating job ETA:', error); // Removed log
    throw error;
  }
};

// Update job ETA only (using dedicated endpoint)
export const updateJobETA = async (jobId, newEtaValue) => {
  try {
    // Update job ETA on the server using the dedicated ETA endpoint
    // This endpoint only updates the ETA and doesn't trigger re-acceptance
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/eta`, {
      eta: newEtaValue
    }, {
      headers: getAuthHeader()
    });

    // console.log(`Updated ETA for job ${jobId} to ${newEtaValue} minutes using dedicated endpoint`); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error updating job ETA:', error); // Removed log
    throw error;
  }
};

// Delete job permanently
export const deleteJob = async (jobId) => {
  try {
    // Delete job on the server
    const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`, {
      headers: getAuthHeader()
    });

    // console.log(`Job ${jobId} deleted successfully:`, response.data); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error deleting job:', error); // Removed log
    throw error;
  }
};

// Duplicate job
export const duplicateJob = async (jobId) => {
  try {
    // Duplicate job on the server
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/duplicate`, {}, {
      headers: getAuthHeader()
    });

    // console.log(`Job ${jobId} duplicated successfully:`, response.data); // Removed log
    return response.data;
  } catch (error) {
    // console.error('Error duplicating job:', error); // Removed log
    throw error;
   }
 };

// NEW: Cancel SP Assignment and Reassign
export const cancelSPAssignment = async (jobId) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/cancel-sp-assignment`, {}, { // POST request with empty body
      headers: getAuthHeader()
    });
    // console.log(`SP assignment for job ${jobId} cancelled successfully:`, response.data); // Removed log
    return response.data; // Return the response which might include the updated job and a message
  } catch (error) {
    // console.error(`Error cancelling SP assignment for job ${jobId}:`, error); // Removed log
    throw error; // Re-throw the error to be handled by the caller
  }
};

// NEW: Find Next Org 1 Driver
export const findNextOrg1Driver = async (jobId) => {
  try {
    // Use POST as it modifies the job state by potentially reassigning
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/find-next-org1-driver`, {}, {
      headers: getAuthHeader()
    });
    // console.log(`Find next Org 1 driver request for job ${jobId} completed:`, response.data); // Removed log
    return response.data; // Expecting { message: '...', job: { ... } } or { message: '...' }
  } catch (error) {
    // console.error(`Error finding next Org 1 driver for job ${jobId}:`, error); // Removed log
    throw error; // Re-throw the error to be handled by the caller
  }
};

 // Fetch ranked Service Providers for a specific job
 // Updated to accept jobDetails object
 export const fetchRankedSPsForJob = async (jobDetails) => {
   if (!jobDetails || !jobDetails.id) {
     console.error("fetchRankedSPsForJob: jobDetails with id is required");
     return []; // Return empty array if no jobId
   }
   try {
     // Construct query parameters from jobDetails
     const params = {
       jobId: jobDetails.id,
       service: jobDetails.service,
       classType: jobDetails.classType,
       // Pass location details if available
       ...(jobDetails.serviceLocation?.zip && { serviceZip: jobDetails.serviceLocation.zip }),
       ...(jobDetails.serviceLocation?.lat && { serviceLat: jobDetails.serviceLocation.lat }),
       ...(jobDetails.serviceLocation?.lng && { serviceLng: jobDetails.serviceLocation.lng }),
       ...(jobDetails.dropoffLocation?.zip && { dropoffZip: jobDetails.dropoffLocation.zip }),
       ...(jobDetails.dropoffLocation?.lat && { dropoffLat: jobDetails.dropoffLocation.lat }),
       ...(jobDetails.dropoffLocation?.lng && { dropoffLng: jobDetails.dropoffLocation.lng }),
     };
     console.log("[fetchRankedSPsForJob] Calling API with params:", params);

     const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sps/ranked-for-job`, {
       params: params, // Send details as query parameters
       headers: getAuthHeader()
     });
     return response.data;
   } catch (error) {
     console.error(`Error fetching ranked SPs for job ${jobDetails.id}:`, error);
     // Return empty array or throw error depending on how frontend should handle
     return [];
   }
 };

 // NEW: Toggle Automated Dispatch for a job
 export const toggleAutomatedDispatch = async (jobId) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/toggle-auto-dispatch`, {}, {
      headers: getAuthHeader()
    });
    // console.log(`Toggled automated dispatch for job ${jobId}:`, response.data); // Removed log
    return response.data; // Return { message, job }
  } catch (error) {
    // console.error(`Error toggling automated dispatch for job ${jobId}:`, error); // Removed log
    throw error;
  }
};

 // Process jobUpdated event for real-time job updates
export const processJobUpdate = (updatedJob, existingJobs) => {
  if (!updatedJob) return existingJobs;

  let createdDisplay;

  // Check if createdAt exists and is a valid date
  if (updatedJob.createdAt && !isNaN(new Date(updatedJob.createdAt).getTime())) {
    const createdDate = new Date(updatedJob.createdAt);
    const today = new Date();

    // Compare year, month, and day to determine if it's today
    const isCreatedToday =
      createdDate.getFullYear() === today.getFullYear() &&
      createdDate.getMonth() === today.getMonth() &&
      createdDate.getDate() === today.getDate();

    if (isCreatedToday) {
      // If created today, show time
      createdDisplay = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      // If created on a different day, show date
      createdDisplay = createdDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  } else {
    // Fallback to the existing created field or 'Unknown'
    createdDisplay = updatedJob.created || 'Unknown';
  }

  // Create a processed job object with the expanded property preserved
  const processedJob = {
    ...updatedJob,
    // Preserve expanded state if the job already exists in our state
    expanded: existingJobs.find(job => job.id === updatedJob.id)?.expanded || false,
    created: createdDisplay
  };

  // Check if the job already exists in the array
  const jobExists = existingJobs.some(job => job.id === updatedJob.id);

  if (jobExists) {
    // Update the existing job
    return existingJobs.map(job =>
      job.id === updatedJob.id ? processedJob : job
    );
  } else {
    // Add the new job to the array
    const finalState = [...existingJobs, processedJob];
    return finalState;
  }
};

/**
 * Request customer location via SMS
 * @param {string} jobId Job ID
 * @param {string} customerPhone Customer phone number
 * @returns {Promise<Object>} API response
 */
export const requestCustomerLocation = async (jobId, customerPhone) => {
    if (!jobId || !customerPhone) {
        return { success: false, message: 'Job ID and customer phone number are required.' };
    }
    try {
        // Corrected endpoint path to /api/jobs/ (Removed /v1)
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/request-location`,
            { customerPhone }, // Send phone number in the body
            { headers: getAuthHeader() }
        );
        return {
            success: true,
            message: response.data.message || 'Location request sent successfully.',
            data: response.data
        };
    } catch (error) {
        // console.error('Error requesting customer location:', error); // Removed log
        return {
            success: false,
            message: error.response?.data?.message || 'Failed to request customer location.',
            error
        };
    }
};

// Unassign job from Service Provider
export const unassignFromServiceProvider = async (jobId) => {
  try {
    // Call the backend endpoint to unassign the job from SP
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`, {
      assignedVendorNumber: null
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    console.error('Error unassigning job from service provider:', error);
    throw error;
  }
};

// Unassign only the driver (for Org 2 users)
export const unassignDriverOnly = async (jobId) => {
  try {
    // Call the backend endpoint to unassign only the driver, keeping SP assignment
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`, {
      driverId: null,
      driverName: null,
      truck: null
    }, {
      headers: getAuthHeader()
    });

    return response.data;
  } catch (error) {
    console.error('Error unassigning driver only:', error);
    throw error;
  }
};

// No need for separate export block when using `export const` for each function
